import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { locales } from "@gioia365/i18n";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { authApi, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, type AuthFailure } from "../api/auth";
import { myMembershipsQuery, useMe, useUpdateMe } from "../api/queries";
import { GoogleCalendarCard } from "../account/GoogleCalendarCard";
import { Button } from "../design/components/Button";
import { Drawer } from "../design/components/Drawer";
import { missingNamePart, NameFields, type NamePart } from "../design/components/NameFields";
import { Select } from "../design/components/Select";
import { TextField } from "../design/components/TextField";
import "../design/components/button.css";
import { messageForError } from "../i18n/errors";
import { useLocale, useT } from "../i18n/locale";
import { useOptionalAccess } from "../session/access";
import { signOutTab } from "../session/switch-tab";
import { describeAuthFailure } from "./auth-failure";
import "./auth.css";
import "./account-drawer.css";

// «Il tuo account» (piano M1b Task 5), dal pulsante in fondo alla rail: nome e cognome dell'account, lingua, password,
// azienda di questa scheda ed «Esci». La finestra è modale e rende inerte il resto della pagina, avvisi
// compresi (D16): l'esito di ciò che si fa qui si legge qui, in una riga `role="status"`. Ogni sezione nasce a
// ogni apertura, quindi i campi non ricordano la volta prima.

type Props = { open: boolean; onClose: () => void };

export function AccountDrawer({ open, onClose }: Props) {
  const t = useT();
  const router = useRouter();
  const [signingOut, setSigningOut] = useState(false);

  async function signOut() {
    setSigningOut(true);
    try {
      await signOutTab(router);
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <Drawer
      open={open}
      title={t("account.title")}
      onClose={onClose}
      footer={
        <Button onClick={() => void signOut()} disabled={signingOut}>
          {signingOut ? t("common.signingOut") : t("common.signOut")}
        </Button>
      }
    >
      <NameSection />
      <LanguageSection />
      <PasswordSection />
      <GoogleCalendarCard />
      <CompanySection />
    </Drawer>
  );
}

/**
 * Un nome intero diviso come la migrazione dei due campi: la prima parola è il nome, il resto il cognome (vuoto
 * se la parola è una sola).
 */
function splitFullName(name: string): [string, string] {
  const [first = "", ...rest] = name.trim().split(/\s+/);
  return [first, rest.join(" ")];
}

/**
 * Nome e cognome dell'identità (`PATCH /api/me { firstName, lastName }`); quelli con cui la persona compare nel team
 * sono altri. Un'identità nata prima dei due campi non li ha: le caselle partono dal nome intero diviso e la persona
 * li corregge; finché non li salva c'è sempre qualcosa da salvare.
 */
function NameSection() {
  const t = useT();
  const me = useMe();
  const access = useOptionalAccess();
  const updateMe = useUpdateMe();
  const stored = me.firstName !== null && me.lastName !== null ? { firstName: me.firstName, lastName: me.lastName } : null;
  const [initialFirst, initialLast] = stored ? [stored.firstName, stored.lastName] : splitFullName(me.name);
  const [firstName, setFirstName] = useState(initialFirst);
  const [lastName, setLastName] = useState(initialLast);
  const [missing, setMissing] = useState<NamePart | null>(null);
  const [saved, setSaved] = useState(false);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const first = firstName.trim();
  const last = lastName.trim();
  const unchanged = stored !== null && first === stored.firstName && last === stored.lastName;
  const company = access && !access.isPlatform ? access.context : null;
  const hint = company?.displayName
    ? t("account.name.helpInCompany", { organization: company.organization.name, displayName: company.displayName })
    : t("account.name.help");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (updateMe.isPending) return;
    setSaved(false);
    const empty = missingNamePart(first, last);
    if (empty) {
      updateMe.reset();
      setMissing(empty);
      (empty === "lastName" ? lastNameRef : firstNameRef).current?.focus();
      return;
    }
    setMissing(null);
    try {
      // Tutti e due insieme: un'identità nata prima dei due campi non ha l'altro salvato.
      await updateMe.mutateAsync({ firstName: first, lastName: last });
      setFirstName(first);
      setLastName(last);
      setSaved(true);
    } catch {
      // L'errore resta nella mutation e si legge sotto i campi.
    }
  }

  function change(set: (value: string) => void) {
    return (value: string) => {
      set(value);
      setSaved(false);
    };
  }

  const error = missing ? t("common.nameMissing") : updateMe.isError ? messageForError(t, updateMe.error) : undefined;

  return (
    <form className="g-account__section" onSubmit={(event) => void handleSubmit(event)} aria-busy={updateMe.isPending} noValidate>
      <NameFields
        id="account-name"
        firstName={{ label: t("common.firstName"), value: firstName, onChange: change(setFirstName), ref: firstNameRef }}
        lastName={{ label: t("common.lastName"), value: lastName, onChange: change(setLastName), ref: lastNameRef }}
        autoComplete="person"
        hint={hint}
        error={error}
        invalid={missing ?? "both"}
      />
      <div className="g-account__actions">
        {/* Nulla da salvare finché nome e cognome non cambiano; una casella vuota si spiega al clic. */}
        <Button type="submit" disabled={unchanged || updateMe.isPending}>
          {updateMe.isPending ? t("common.saving") : t("account.name.save")}
        </Button>
        <p className="g-account__status" role="status">{saved ? t("account.name.saved") : null}</p>
      </div>
    </form>
  );
}

/**
 * La lingua della persona, salvata subito (`PATCH /api/me { preferredLocale }`) e applicata da `LocaleSync`.
 * Soltanto italiano e inglese: nessun «Come l'azienda» (decisione del product owner sulla questione 39).
 */
function LanguageSection() {
  const { t, locale } = useLocale();
  const me = useMe();
  const updateMe = useUpdateMe();
  const [saved, setSaved] = useState(false);
  // Mentre la richiesta viaggia il select mostra già la scelta; senza una scelta, la lingua in uso.
  const value = (updateMe.isPending ? updateMe.variables.preferredLocale : null) ?? me.preferredLocale ?? locale;

  function choose(next: string) {
    const chosen = locales.find((candidate) => candidate === next);
    if (!chosen) return;
    setSaved(false);
    updateMe.mutate({ preferredLocale: chosen }, { onSuccess: () => setSaved(true) });
  }

  return (
    <div className="g-account__section">
      <Select
        id="account-language"
        label={t("account.language.label")}
        value={value}
        onChange={choose}
        error={updateMe.isError ? messageForError(t, updateMe.error) : undefined}
        options={[
          { value: "it", label: t("account.language.italian") },
          { value: "en", label: t("account.language.english") },
        ]}
      />
      <p className="g-account__status" role="status">{saved ? t("account.language.saved") : null}</p>
    </div>
  );
}

type PasswordProblem =
  | { field: "current"; reason: "missing" | "wrong_password" }
  | { field: "new"; reason: "password_too_short" | "password_too_long" }
  | { field: "confirm"; reason: "mismatch" }
  | { field: "form"; reason: AuthFailure };

function passwordLengthProblem(password: string): PasswordProblem | null {
  if (password.length < PASSWORD_MIN_LENGTH) return { field: "new", reason: "password_too_short" };
  if (password.length > PASSWORD_MAX_LENGTH) return { field: "new", reason: "password_too_long" };
  return null;
}

/** «Cambia la password»: controlli di lunghezza e di uguaglianza prima dell'invio, poi Better Auth. */
function PasswordSection() {
  const t = useT();
  const titleId = useId();
  const [editing, setEditing] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  // Codici e non testi, così gli errori seguono il cambio di lingua.
  const [problem, setProblem] = useState<PasswordProblem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const openRef = useRef<HTMLButtonElement>(null);
  const currentRef = useRef<HTMLInputElement>(null);
  const nextRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);

  // All'apertura il focus va sulla password attuale; alla chiusura torna a «Cambia la password».
  useEffect(() => {
    if (editing) currentRef.current?.focus();
    else if (wasEditing.current) openRef.current?.focus();
    wasEditing.current = editing;
  }, [editing]);

  // Dopo una risposta del server il focus torna dove si può correggere, quando il pulsante si riattiva.
  useEffect(() => {
    if (submitting || !problem) return;
    if (problem.field === "current") currentRef.current?.focus();
    else if (problem.field === "new") nextRef.current?.focus();
    else if (problem.field === "confirm") confirmRef.current?.focus();
    else submitRef.current?.focus();
  }, [problem, submitting]);

  function close() {
    setEditing(false);
    setCurrent("");
    setNext("");
    setConfirm("");
    setProblem(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    const found: PasswordProblem | null = !current
      ? { field: "current", reason: "missing" }
      : (passwordLengthProblem(next) ?? (next !== confirm ? { field: "confirm", reason: "mismatch" } : null));
    setProblem(found);
    if (found) return;
    setSubmitting(true);
    const result = await authApi.changePassword({ currentPassword: current, newPassword: next });
    setSubmitting(false);
    if (result.ok) {
      close();
      setDone(true);
      return;
    }
    if (result.failure === "wrong_password") setProblem({ field: "current", reason: "wrong_password" });
    else if (result.failure === "password_too_short" || result.failure === "password_too_long") setProblem({ field: "new", reason: result.failure });
    else setProblem({ field: "form", reason: result.failure });
  }

  function messageFor(field: PasswordProblem["field"]): string | undefined {
    if (!problem || problem.field !== field) return undefined;
    switch (problem.field) {
      case "current":
        return problem.reason === "missing" ? t("account.password.currentMissing") : describeAuthFailure(t, problem.reason);
      case "confirm":
        return t("auth.resetPassword.mismatch");
      default:
        return describeAuthFailure(t, problem.reason);
    }
  }

  const formFailure = messageFor("form");

  return (
    <section className="g-account__section" aria-labelledby={titleId}>
      <h3 id={titleId}>{t("account.password.title")}</h3>
      {editing ? (
        <form className="g-stack" onSubmit={(event) => void handleSubmit(event)} aria-labelledby={titleId} aria-busy={submitting} noValidate>
          <TextField
            id="account-current-password"
            ref={currentRef}
            label={t("account.password.current")}
            type="password"
            autoComplete="current-password"
            required
            value={current}
            error={messageFor("current")}
            onChange={(event) => setCurrent(event.target.value)}
          />
          <TextField
            id="account-new-password"
            ref={nextRef}
            label={t("auth.resetPassword.newPassword", { min: PASSWORD_MIN_LENGTH })}
            type="password"
            autoComplete="new-password"
            minLength={PASSWORD_MIN_LENGTH}
            required
            value={next}
            error={messageFor("new")}
            onChange={(event) => setNext(event.target.value)}
          />
          <TextField
            id="account-confirm-password"
            ref={confirmRef}
            label={t("auth.resetPassword.confirmPassword")}
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            error={messageFor("confirm")}
            onChange={(event) => setConfirm(event.target.value)}
          />
          {formFailure ? (
            <p className="g-auth__message" data-tone="error" role="alert">
              {formFailure}
            </p>
          ) : null}
          <div className="g-account__actions">
            <Button ref={submitRef} variant="primary" type="submit" disabled={submitting}>
              {submitting ? t("auth.resetPassword.submitting") : t("auth.resetPassword.submit")}
            </Button>
            <Button onClick={close} disabled={submitting}>{t("common.cancel")}</Button>
          </div>
        </form>
      ) : (
        <div className="g-account__actions">
          <Button
            ref={openRef}
            onClick={() => {
              setDone(false);
              setEditing(true);
            }}
          >
            {t("account.password.change")}
          </Button>
        </div>
      )}
      <p className="g-account__status" role="status">{done ? t("account.password.done") : null}</p>
    </section>
  );
}

/**
 * L'azienda in cui lavora questa scheda, con «Cambia azienda» quando c'è davvero un'altra scelta: un'altra
 * azienda della persona, oppure il Super admin di Gioia365, che può entrare in ogni azienda.
 */
function CompanySection() {
  const t = useT();
  const me = useMe();
  const access = useOptionalAccess();
  const titleId = useId();
  const memberships = useQuery(myMembershipsQuery).data;
  const context = access?.context ?? null;
  const canChange = me.isPlatformAdmin || (memberships ?? []).some((membership) => membership.membershipId !== context?.membershipId);
  const change = canChange ? (
    <div className="g-account__actions">
      <Link to="/scegli-azienda" className="g-button" data-variant="secondary">{t("account.company.change")}</Link>
    </div>
  ) : null;

  if (context && access?.isPlatform) {
    return (
      <div className="g-account__section">
        <p>{t("account.company.platform", { organization: context.organization.name })}</p>
        {change}
      </div>
    );
  }
  if (context) {
    return (
      <section className="g-account__section" aria-labelledby={titleId}>
        <h3 id={titleId}>{t("account.company.title")}</h3>
        <p>{t("account.company.current", { organization: context.organization.name, role: context.role?.name ?? "" })}</p>
        {change}
      </section>
    );
  }
  // Nessuna azienda nella scheda (pagine della piattaforma): soltanto la scelta.
  return change ? <div className="g-account__section">{change}</div> : null;
}
